




import { useState, useEffect } from "react";
import { adminLeaveReportApi } from "../../app/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FileSearch,
  Filter,
  User,
  Calendar,
  Tag,
  Activity,
  Download,
  RotateCcw,
  ChevronRight,
  LayoutList,
  Search,
  X
} from "lucide-react";

/* DATE FORMATTER */
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function LeaveReport() {
  const navigate = useNavigate();
  const [report, setReport] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    status: "",
    start_date: "",
    end_date: "",
    leave_type: "",
    search: "",
  });

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFilters({
      status: "",
      start_date: "",
      end_date: "",
      leave_type: "",
      search: "",
    });
  };

  const loadReport = async () => {
    setIsLoading(true);
    const res = await adminLeaveReportApi();
    if (res.success) {
      setReport(res.data || []);
    } else {
      toast.error("Failed to load report");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadReport();
  }, []);

  const filteredReport = report.filter((r) => {
    const matchesSearch = filters.search === "" || r.employee_name?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesLeaveType = filters.leave_type === "" || r.leave_type === filters.leave_type;
    const matchesStatus = filters.status === "" || Number(r.approval_status) === Number(filters.status);
    const matchesStartDate = filters.start_date === "" || r.leave_start_date >= filters.start_date;
    const matchesEndDate = filters.end_date === "" || r.leave_end_date <= filters.end_date;

    return matchesSearch && matchesLeaveType && matchesStatus && matchesStartDate && matchesEndDate;
  });

  const getStatusBadge = (status) => {
    if (status === 1)
      return <span className="px-2.5 py-1 rounded-lg text-[9px] font-semibold uppercase tracking-widest bg-green-500/10 border border-green-500/20 text-green-400">Approved</span>;
    if (status === 0)
      return <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>;
    return <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-yellow-500/10 border border-yellow-500/20 text-yellow-500">Pending</span>;
  };

  return (
    <div className="max-w-8xl mx-auto md:mt-20 md:mb-20">

      <div className="
            relative overflow-hidden
            rounded-[20px] p-8 md:p-10
            bg-gradient-to-br from-[#1e174a] to-[#0f0c29]
            backdrop-blur-2xl text-white
            border border-white/10
            shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
          ">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 px-2 text-white">
          <div className="flex items-center gap-4">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl shadow-indigo-500/20 md:mt-0 mt-[-24px]">
              <LayoutList className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-white whitespace-nowrap">Leave Reports</h1>
              <p className="text-white/60 text-sm">Analyze and overview the leave history of your entire team</p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-2xl transition-all duration-300 font-bold text-xs uppercase tracking-widest ${showFilters ? 'bg-white text-black' : 'bg-white/5 border border-white/10 text-white'}`}
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            {/* <button className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 transition-all shadow-xl shadow-blue-500/20 font-bold text-xs uppercase tracking-widest">
            <Download className="w-4 h-4" />
            Export Data
          </button> */}
          </div>
        </div>

        {/* FILTER DRAWER */}
        {showFilters && (
          <div className="mb-4 mt-[-14px] p-4 rounded-lg bg-white/5 border border-white/10 backdrop-blur-3xl animate-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-2 md:gap-4">
              {/* <FilterGroup label="Search Employee" icon={<Search className="w-3.5 h-3.5" />}> */}
              <input
                name="search"
                placeholder="Search Employee..."
                onChange={handleChange}
                value={filters.search}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-5 py-1 text-sm text-white placeholder-white/60 outline-none focus:border-indigo-500/50"
              />
              {/* </FilterGroup> */}

              {/* <FilterGroup label="Leave Type" icon={<Tag className="w-3.5 h-3.5" />}> */}
              <select
                name="leave_type"
                onChange={handleChange}
                value={filters.leave_type}
                className="w-full bg-[#1b1040] border border-white/10 rounded-lg px-5 py-1 text-sm text-white outline-none focus:border-indigo-500/50 appearance-none cursor-pointer"
              >
                <option value="">Leave Type</option>
                <option value="Casual">Casual</option>
                <option value="Unpaid Leave">Leave Without Pay [LWP]</option>
                <option value="Half Day">Half Day</option>
                <option value="Short Leave">Short Leave</option>
                <option value="Work From Home">Work From Home</option>


              </select>
              {/* </FilterGroup> */}

              {/* <FilterGroup label="Application Status" icon={<Activity className="w-3.5 h-3.5" />}> */}
              <select
                name="status"
                onChange={handleChange}
                value={filters.status}
                className="w-full bg-[#1b1040] border border-white/10 rounded-lg px-5 py-1 text-sm text-white outline-none focus:border-indigo-500/50 appearance-none cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="1">Approved</option>
                <option value="0">Rejected</option>
                <option value="pending">Pending</option>
              </select>
              {/* </FilterGroup> */}

              {/* <FilterGroup label="From Date" icon={<Calendar className="w-3.5 h-3.5" />}> */}

              <DatePicker
                selected={filters.start_date ? new Date(filters.start_date) : null}
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    start_date: date ? date.toISOString().split("T")[0] : "",
                  })
                }
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar=" "
                // className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
                calendarClassName="custom-calendar"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={6}
                placeholderText="Start Date"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-5 py-1 text-sm text-white placeholder-white/20 outline-none focus:border-indigo-500/50"


                portalId="root"        // ✅ KEY FIX
                popperPlacement="bottom-start"
                popperClassName="z-[9999]"   // ✅ ensure on top
              />
              {/* </FilterGroup> */}

              {/* <FilterGroup label="To Date" icon={<Calendar className="w-3.5 h-3.5" />}> */}
              <DatePicker
                selected={filters.end_date ? new Date(filters.end_date) : null}
                onChange={(date) =>
                  setFilters({
                    ...filters,
                    end_date: date ? date.toISOString().split("T")[0] : "",
                  })
                }
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar=" "
                // className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
                calendarClassName="custom-calendar"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={6}
                placeholderText="End Date"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-5 py-1 text-sm text-white placeholder-white/20 outline-none focus:border-indigo-500/50"


                portalId="root"        // ✅ KEY FIX
                popperPlacement="bottom-start"
                popperClassName="z-[9999]"   // ✅ ensure on top

              />
              {/* </FilterGroup> */}
            </div>

            <div className=" mt-2 border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest flex items-center gap-2">
              <Activity className="w-3 h-3" />
              Found {filteredReport.length} historical records
            </p> */}
              <div className="flex gap-4 w-full sm:w-auto">
                <button
                  onClick={handleReset}
                  className="flex-1 sm:flex-none whitespace-nowrap py-1.5 flex items-center justify-center gap-2 md:gap-1 px-2 md:px-10 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white transition-all text-sm font-bold"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset Filters
                </button>
                <button
                  onClick={loadReport}
                  className="flex-1 sm:flex-none px-3 py-1 md:px-10 md:py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-all shadow-lg shadow-indigo-500/20 text-xs font-black uppercase tracking-widest"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AUDIT DATA TABLE */}
        <div className="overflow-hidden rounded-lg bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-white border-collapse">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/10">
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Employee </th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Category</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Period Start</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Period End</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Duration</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Reason</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Remarks</th>
                  <th className="p-3 text-left text-sm text-white/60 whitespace-nowrap">Decision</th>
                  {/* <th className="p-6 text-[10px] font-black uppercase tracking-[2px] text-white/30 text-center whitespace-nowrap">Audit</th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 whitespace-nowrap">
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                        <span className="text-xs font-bold text-white/30 uppercase tracking-widest">Crunching data...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredReport.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4 opacity-10">
                        <FileSearch className="w-20 h-20" />
                        <span className="text-sm font-black uppercase tracking-widest">No audit trails matched your criteria</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredReport.map((r) => (
                    <tr key={r.id} className="group hover:bg-white/[0.04] transition-all duration-300">
                      <td className="p-3">
                        <button
                          onClick={() => navigate(`/users/${r.employee_id}`)}
                          className="flex items-center gap-3 text-white/80 hover:text-indigo-400 transition-colors text-left"
                        >
                          <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                            <User className="w-3 h-3" />
                          </div>
                          <div>
                            <p className="text-sm  text-white/70  font-semibold tracking-tight">{r.employee_name}</p>
                            {/* <p className="text-[9px] font-bold text-white/20 uppercase">Audit ID #342</p> */}
                          </div>
                        </button>
                      </td>
                      <td className="p-3">
                        <p className="text-[13px] font-semibold text-white/70">
                          {r.leave_type === "Unpaid Leave" ? "Leave Without Pay [LWP]" : r.leave_type}
                        </p>
                      </td>
                      <td className="p-3">
                        <p className="text-xs font-medium text-white/60">{formatDate(r.leave_start_date)}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-xs font-medium text-white/60">{formatDate(r.leave_end_date)}</p>
                      </td>
                      <td className="p-3 text-center">
                        <div className="inline-flex items-baseline gap-1 bg-white/5 px-3 py-1 rounded-lg">
                          <span className="text-sm font-semibold">{r.duration}</span>
                          <span className="text-[9px] font-bold text-white/30 uppercase">Days</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-white/60 italic max-w-[200px] truncate" title={r.employee_comment}>
                          {r.employee_comment || "-"}
                        </p>
                      </td>
                      <td className="p-3">
                        {r.remarks ? (
                          <p className="text-xs text-amber-300/80 italic max-w-[220px] truncate" title={r.remarks}>
                            {r.remarks}
                          </p>
                        ) : (
                          <span className="text-white/20 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center ">
                        {getStatusBadge(r.approval_status)}
                      </td>
                      {/* <td className="p-6 text-center">
                      <button className="p-3 rounded-2xl bg-white/5 hover:bg-indigo-500/20 text-white/20 hover:text-indigo-400 transition-all">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, icon, children }) {
  return (
    <div className="space-y-2 group">
      <div className="flex items-center gap-2 ml-1">
        <span className="text-blue-400 opacity-50 group-focus-within:opacity-100 transition-opacity">{icon}</span>
        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 group-focus-within:text-white transition-colors">{label}</label>
      </div>
      {children}
    </div>
  );
}







// import { useState, useEffect } from "react";
// import { adminLeaveReportApi } from "../../app/api";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";



// /* DATE FORMATTER */
// const formatDate = (date) => {
//   if (!date) return "-";

//   return new Date(date).toLocaleDateString("en-GB", {
//     day: "2-digit",
//     month: "short",
//     year: "numeric",
//   });
// };

// export default function LeaveReport() {
//   const navigate = useNavigate();

//   const [filters, setFilters] = useState({
//     status: "",
//     start_date: "",
//     end_date: "",
//     leave_type: "",
//     search: "",
//   });

//   const [report, setReport] = useState([]);

//   const handleChange = (e) => {
//     setFilters({ ...filters, [e.target.name]: e.target.value });
//   };


//   const handleReset = () => {
//     setFilters({
//       status: "",
//       start_date: "",
//       end_date: "",
//       leave_type: "",
//       search: "",
//     });
//   };

//   // const loadReport = async () => {
//   //   const res = await adminLeaveReportApi(filters);
//   //   if (res.success) {
//   //     setReport(res.data || []);
//   //   } else {
//   //     toast.error("Failed to load report");
//   //   }
//   // };


//   const loadReport = async () => {
//     const res = await adminLeaveReportApi();
//     if (res.success) {
//       setReport(res.data || []);
//     } else {
//       toast.error("Failed to load report");
//     }
//   };

//   useEffect(() => {
//     loadReport();
//   }, []);




//   const filteredReport = report.filter((r) => {

//     const matchesSearch =
//       filters.search === "" ||
//       r.employee_name
//         ?.toLowerCase()
//         .includes(filters.search.toLowerCase());

//     const matchesLeaveType =
//       filters.leave_type === "" ||
//       r.leave_type === filters.leave_type;

//     const matchesStatus =
//       filters.status === "" ||
//       Number(r.approval_status) === Number(filters.status);

//     const matchesStartDate =
//       filters.start_date === "" ||
//       r.leave_start_date >= filters.start_date;

//     const matchesEndDate =
//       filters.end_date === "" ||
//       r.leave_end_date <= filters.end_date;

//     return (
//       matchesSearch &&
//       matchesLeaveType &&
//       matchesStatus &&
//       matchesStartDate &&
//       matchesEndDate
//     );
//   });

//   /* STATUS BADGE */
//   const getStatusBadge = (status) => {
//     if (status === 1)
//       return (
//         <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
//           Approved
//         </span>
//       );

//     if (status === 0)
//       return (
//         <span className="px-3 py-1 rounded-full text-xs bg-red-500/20 text-red-300">
//           Rejected
//         </span>
//       );

//     return (
//       <span className="px-3 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-300">
//         Pending
//       </span>
//     );
//   };










//   return (
//     <div className="rounded-2xl p-6 md:mt-20 bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f] text-white shadow-2xl">

//       <h2 className="font-bold text-2xl mb-6">
//         Leave Report (Admin)
//       </h2>

//       {/* FILTERS */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6 text-sm">
//         <input
//           name="search"
//           placeholder="Search employee"
//           onChange={handleChange}
//           value={filters.search}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
//         />

//         <select
//           name="leave_type"
//           onChange={handleChange}
//           value={filters.leave_type}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         >
//           <option value="">Leave Type</option>
//           <option value="Casual Leave">Casual Leave</option>
//           <option value="Half Day">Half Day</option>
//           <option value="Short Leave">Short Leave</option>
//           <option value="Work From Home">Work From Home</option>
//         </select>

//         <select
//           name="status"
//           onChange={handleChange}
//           value={filters.status}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         >
//           <option value="">All Status</option>
//           <option value="1">Approved</option>
//           <option value="0">Rejected</option>
//         </select>

//         {/* <input
//           type="date"
//           name="start_date"
//           onChange={handleChange}
//           value={filters.start_date}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         />

//         <input
//           type="date"
//           name="end_date"
//           onChange={handleChange}
//           value={filters.end_date}
//           className="px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//         /> */}


//         {/* START DATE */}
//         {/* <DatePicker
//   selected={filters.start_date ? new Date(filters.start_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       start_date: date
//         ? date.toISOString().split("T")[0]
//         : "",
//     })
//   }
//   placeholderText="Start Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// /> */}

// <DatePicker
//   selected={filters.start_date ? new Date(filters.start_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       start_date: date ? date.toISOString().split("T")[0] : "",
//     })
//   }
//   dateFormat="yyyy-MM-dd"
//   dateFormatCalendar=" "
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
//   calendarClassName="custom-calendar"
//   showMonthDropdown
//   showYearDropdown
//   scrollableYearDropdown
//   yearDropdownItemNumber={6}
//   placeholderText="Start Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// />

//         {/* END DATE */}
//         {/* <DatePicker
//   selected={filters.end_date ? new Date(filters.end_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       end_date: date
//         ? date.toISOString().split("T")[0]
//         : "",
//     })
//   }
//   placeholderText="End Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// /> */}

// <DatePicker
//   selected={filters.end_date ? new Date(filters.end_date) : null}
//   onChange={(date) =>
//     setFilters({
//       ...filters,
//       end_date: date ? date.toISOString().split("T")[0] : "",
//     })
//   }
//   dateFormat="yyyy-MM-dd"
//   dateFormatCalendar=" "
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer"
//   calendarClassName="custom-calendar"
//   showMonthDropdown
//   showYearDropdown
//   scrollableYearDropdown
//   yearDropdownItemNumber={6}
//   placeholderText="End Date"
//   className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
// />


//       </div>
//       <div className="flex gap-3 mb-6">
//         <button
//           onClick={loadReport}
//           className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm shadow-lg"
//         >
//           Filter Report
//         </button>

//         <button
//           onClick={handleReset}
//           className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm shadow-lg"
//         >
//           Reset
//         </button>
//       </div>

//       {/* ================= DESKTOP TABLE ================= */}
//       <div className="hidden md:block overflow-x-auto">
//         <table className="min-w-[900px] w-full text-sm">
//           <thead className="text-white/70 border-b border-white/10">
//             <tr>
//               <th className="p-3 text-left">Employee</th>
//               <th className="p-3 text-left">Leave Type</th>
//               <th className="p-3 text-left">Start</th>
//               <th className="p-3 text-left">End</th>
//               <th className="p-3">Days</th>
//               <th className="p-3">Status</th>
//             </tr>
//           </thead>

//           <tbody>
//             {/* {report.length === 0 ? ( */}
//             {filteredReport.length === 0 ? (
//               <tr>
//                 <td colSpan="6" className="py-8 text-center opacity-60">
//                   No records found
//                 </td>
//               </tr>
//             ) : (
//               // report.map((r) => (
//               filteredReport.map((r) => (
//                 <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
//                   <td className="p-3 font-medium">
//                     <button
//                       onClick={() => navigate(`/users/${r.employee_id}`)}
//                       // onClick={() => navigate(`/users/${leave.employee_id}`)}
//                       className="text-blue-300 hover:underline"
//                     >
//                       {r.employee_name}
//                     </button>
//                   </td>

//                   <td className="p-3">
//                     {r.leave_type}
//                   </td>

//                   <td className="p-3">
//                     {formatDate(r.leave_start_date)}
//                   </td>

//                   <td className="p-3">
//                     {formatDate(r.leave_end_date)}
//                   </td>

//                   <td className="p-3 text-center">
//                     {r.duration}
//                   </td>

//                   <td className="p-3 text-center">
//                     {getStatusBadge(r.approval_status)}
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* ================= MOBILE CARDS ================= */}
//       <div className="md:hidden space-y-3">
//         {filteredReport.map((r) => (
//           // {/* filteredReport.map((r) => ( */}
//           <div
//             key={r.id}
//             className="bg-gradient-to-br from-[#2d1c6a] to-[#12082f] border border-white/10 rounded-2xl p-4 shadow-lg"
//           >
//             <div className="flex justify-between mb-2">
//               <p
//                 className="font-semibold text-blue-300 cursor-pointer"
//                 onClick={() => navigate(`/users/${r.employee_id}`)}
//               // employee_id
//               >
//                 {r.employee_name}
//               </p>
//               {getStatusBadge(r.approval_status)}
//             </div>

//             <div className="grid grid-cols-2 gap-3 text-sm">
//               <div>
//                 <p className="text-white/60 text-xs">Leave Type</p>
//                 <p>{r.leave_type}</p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">Days</p>
//                 <p className="font-semibold text-blue-300">
//                   {r.duration}
//                 </p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">Start</p>
//                 <p>{formatDate(r.leave_start_date)}</p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">End</p>
//                 <p>{formatDate(r.leave_end_date)}</p>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
